/**
 * Players: the ESPN player registry and the season-wide player read.
 *
 * Extracted from the 4,132-line `services/supabaseDataManager.js` god class.
 * Every function takes the shared `ctx` ({ client, seasonsCache, activeSeasonId })
 * as its first argument; see `./context.js`.
 */

import { formatFromDatabase } from './caseMap.js';
import { throwDbError } from './errors.js';
import { mapESPNInjuryStatus } from './espnMapping.js';
export async function syncPlayerFromESPN(ctx, espnPlayerId, name, position, nflTeam, playerStats = {}) {

  try {
    // Build player data object
    const playerData = {
      espn_player_id: espnPlayerId,
      name: name,
      position: position,
      team_abbreviation: nflTeam,
      is_active: true,
      updated_at: new Date().toISOString()
    };

    // Add points data if provided
    if (playerStats.seasonProjectedPoints !== undefined) {
      playerData.season_projected_points = playerStats.seasonProjectedPoints;
    }
    if (playerStats.seasonActualPoints !== undefined) {
      playerData.season_actual_points = playerStats.seasonActualPoints;
    }
    if (playerStats.projectedPoints !== undefined) {
      playerData.projected_points = playerStats.projectedPoints;
    }
    if (playerStats.actualPoints !== undefined) {
      playerData.actual_points = playerStats.actualPoints;
    }
    if (playerStats.gamesPlayed !== undefined) {
      playerData.games_played = playerStats.gamesPlayed;
    }
    if (playerStats.injuryStatus !== undefined) {
      playerData.injury_status = mapESPNInjuryStatus(playerStats.injuryStatus);
    }
    if (playerStats.percentOwned !== undefined) {
      playerData.percent_owned = playerStats.percentOwned;
    }
    if (playerStats.percentStarted !== undefined) {
      playerData.percent_started = playerStats.percentStarted;
    }
    if (playerStats.proTeamName !== undefined) {
      playerData.pro_team_name = playerStats.proTeamName;
    }
    if (playerStats.proTeam !== undefined) {
      playerData.pro_team_id = playerStats.proTeam;
    }

    // Add sync timestamp if we have stats data
    if (Object.keys(playerStats).length > 0) {
      playerData.last_stats_sync = new Date().toISOString();
      playerData.espn_last_updated = new Date().toISOString();
    }

    // Insert or update player
    const { data, error } = await ctx.client
      .from('players')
      .upsert(playerData, {
        onConflict: 'espn_player_id'
      })
      .select('id')
      .single();

    if (error) throw error;

    return data.id;
  } catch (error) {
    throwDbError(error, 'Sync player from ESPN');
  }
}

export async function getAllPlayers(ctx, seasonId = null) {

  try {
    let query = ctx.client
      .from('players')
      .select(`
        id,
        espn_player_id,
        name,
        position,
        team_abbreviation,
        jersey_number,
        is_active,
        projected_points,
        actual_points,
        season_projected_points,
        season_actual_points,
        games_played,
        average_points_per_game,
        projected_average,
        injury_status,
        percent_owned,
        percent_started,
        pro_team_id,
        pro_team_name,
        last_stats_sync,
        espn_last_updated
      `)
      .eq('is_active', true)
      .order('season_projected_points', { ascending: false });

    // If seasonId is provided, get only players rostered in that season
    if (seasonId) {
      query = ctx.client
        .from('rosters')
        .select(`
          team_id,
          roster_slot,
          acquisition_type,
          acquisition_week,
          added_date,
          cost,
          is_keeper,
          team:teams!inner (
            id,
            name,
            season_id
          ),
          player:players (
            id,
            espn_player_id,
            name,
            position,
            team_abbreviation,
            jersey_number,
            is_active,
            projected_points,
            actual_points,
            season_projected_points,
            season_actual_points,
            games_played,
            average_points_per_game,
            projected_average,
            injury_status,
            percent_owned,
            percent_started,
            pro_team_id,
            pro_team_name,
            last_stats_sync,
            espn_last_updated
          )
        `)
        .eq('team.season_id', seasonId);
    }

    const { data, error } = await query;

    if (error) throw error;

    // If seasonId was provided, extract players from roster data
    if (seasonId) {
      const playersMap = new Map();
      (data || []).forEach(rosterEntry => {
        const player = rosterEntry.player;
        if (player && !playersMap.has(player.id)) {
          playersMap.set(player.id, formatFromDatabase(player));
        }
      });
      return Array.from(playersMap.values());
    }

    return formatFromDatabase(data || []);
  } catch (error) {
    throwDbError(error, 'Get all players');
  }
}

/**
 * Players whose name contains `query`, most-owned first.
 *
 * For the parlay's autocomplete, which needs a *bounded* answer: the
 * no-season branch of `getAllPlayers` above selects every active player in the
 * database with no limit, which is thousands of rows to fill a ten-row list.
 *
 * Under two characters this returns `[]` without a network call. "a" matches
 * most of the NFL and ranks it by ownership, which is a list of famous players
 * rather than an answer; the request is not worth making.
 *
 * Ordered by `percent_owned` because a member typing "jefferson" means the
 * one everybody rosters. Nulls sort last so an unsynced player cannot lead.
 */
export async function searchPlayers(ctx, query, { limit = 10 } = {}) {
  const term = (query ?? '').trim();
  if (term.length < 2) return [];

  try {
    // `%` and `_` are wildcards to ILIKE; a name is not a pattern.
    const escaped = term.replace(/[\\%_]/g, (char) => `\\${char}`);

    const { data, error } = await ctx.client
      .from('players')
      .select(`
        id,
        espn_player_id,
        name,
        position,
        team_abbreviation,
        pro_team_id,
        pro_team_name,
        injury_status,
        percent_owned
      `)
      .ilike('name', `%${escaped}%`)
      .eq('is_active', true)
      .order('percent_owned', { ascending: false, nullsFirst: false })
      .limit(limit);

    if (error) throw error;

    return formatFromDatabase(data || []);
  } catch (error) {
    throwDbError(error, 'Search players');
  }
}

/**
 * Players by exact name, for the league chat's data tables (JITS-47): the
 * snapshot names a player but carries no id, so a headshot needs the name
 * looked up. Exact match only; the names in the snapshot are `players.name`.
 * Every row with a name is returned, so the caller can see when two players
 * share one and decline to guess (`uniqueByName` in `utils/playerImages.js`).
 */
export async function getPlayersByNames(ctx, names) {
  const wanted = [...new Set((names ?? []).filter((name) => typeof name === 'string' && name.trim()))];
  if (wanted.length === 0) return [];

  try {
    const rows = [];
    for (let start = 0; start < wanted.length; start += 100) {
      const { data, error } = await ctx.client
        .from('players')
        .select('espn_player_id, name, position, team_abbreviation, pro_team_id')
        .in('name', wanted.slice(start, start + 100));
      if (error) throw error;
      rows.push(...(data || []));
    }
    return formatFromDatabase(rows);
  } catch (error) {
    throwDbError(error, 'Get players by name');
  }
}
